// hooks/useOfertaTarifaForm.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { tarifaSchema } from '../../utils/validationSchemas';
import {
    set_oferta_erp,
    copiar_ctto_actual,
    create_oferta_erp
} from '../../services/ofertas';
import { useTarifas } from './useTarifas';
import { useSubtypesOffer } from './useSubtypesOffer';

/*
Hook principal del formulari de Tarifa per crear/editar una oferta.
Orquestra estat, validacio, preomplert i persistencia contra ERP.
S'utilitza des de OfertaTarifa.js i components del pas de tarifes.
*/

const defaultValues = {
    type_oferta: "",
    sub_type_offer: "",
    tarifa_comercial: "",
    tarifa_oferta: "",
    mode_facturacio: "", //fijo o indexada
    coeficient_k: 10,
    coeficient_d: 7,
    is_call_tarifa: false,
    numero_ctto: "",
    fecha_estimada_inicio_ctto: "",
    showDatePicker: null,
};

/*
Segons el sector (electric o gas), extreu l'id de la tarifa d'acces
desada a l'oferta ERP per preomplir el formulari.
*/
const getTarifaOfertaValue = (erp, tipo) => {
    const val = tipo === "electrico" ? erp.tarifa_oferta : erp.tarifa_oferta_gas;
    return Array.isArray(val) ? Number(val[0]) : Number(val);
};

// Retorna la llista de preus correcta segons sector (electric o gas).
const getListaPrecios = (erp, tipo) =>
    tipo === "electrico" ? erp.llista_preu : erp.llista_preu_gas;

// Normalitza el subtype a un id pla (array/objecte/valor simple).
const extractSubtypeId = (value) => {
    if (Array.isArray(value)) {
        return value.length > 0 ? value[0] : "";
    }
    if (value && typeof value === 'object') {
        return value.id ?? value.value ?? "";
    }
    return value ?? "";
};

// Converteix valors de data a Date (admet Date, YYYY-MM-DD i valors parsejables).
const parseDateOnly = (value) => {
    if (!value) return null;

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
        if (dateOnlyMatch) {
            const [, y, m, d] = dateOnlyMatch;
            return new Date(Number(y), Number(m) - 1, Number(d));
        }
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Data en format YYYY-MM-DD per enviar-la a backend/ERP.
const formatDateOnly = (value) => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const useOfertaTarifaForm = ({ sector_type, oferta_id, ofertas_erp, user }) => {
    const isRestrictedCallAgent = Boolean(user?.is_call_agent && !user?.is_superadmin);

    const {
        register,
        handleSubmit,
        control,
        watch,
        getValues,
        setValue,
        reset,
        formState: { errors },
    } = useForm({
        mode: "onSubmit",
        defaultValues,
        resolver: yupResolver(tarifaSchema(sector_type)),
    });

    const [submissionError, setSubmissionError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submitLockRef = useRef(false);
    const createdOfertaIdRef = useRef(null);
    const typeOferta = watch("type_oferta");
    const subTypeOffer = watch("sub_type_offer");
    const modeFacturacio = watch("mode_facturacio");
    const tarifaOferta = watch("tarifa_oferta");
    const showDatePicker = watch("showDatePicker");
    const hasInitialized = useRef(false);
    const { tarifas, enableTarifas, loading: loadingTarifas, disableTF } = useTarifas(sector_type);
    const { sub_types_offer_options, loadSubtypes, loading: loadingSubtypes } = useSubtypesOffer();

    // Inicialitza el formulari quan hi ha oferta existent: carrega subtipus,
    // preomple valors, activa tarifes i evita reinicialitzacions posteriors.
    useEffect(() => {
        const initializeForm = async () => {
            if (!ofertas_erp || !oferta_id || hasInitialized.current) return;

            const subtypes = await loadSubtypes(sector_type, ofertas_erp.type || "");
            const subtypeId = extractSubtypeId(ofertas_erp.sub_type_offer);
            const validSubType = subtypes.find(opt => String(opt.id) === String(subtypeId));
            const initialActivationDate =
                sector_type === "electrico"
                    ? ofertas_erp.fecha_estimada_inicio_ctto
                    : ofertas_erp.fecha_estimada_inicio_ctto_gas;

            reset({
                ...defaultValues,
                ...ofertas_erp,
                type_oferta: ofertas_erp.type || "",
                sub_type_offer: validSubType ? subtypeId : "",
                tarifa_oferta: getTarifaOfertaValue(ofertas_erp, sector_type),
                coeficient_k: (sector_type === "electrico" ? ofertas_erp.coeficient_k : ofertas_erp.gas_coeficient_k) || 10,
                mode_facturacio: (sector_type === "electrico" ? ofertas_erp.mode_facturacio : ofertas_erp.gas_mode_facturacio) || "",
                fecha_estimada_inicio_ctto: parseDateOnly(initialActivationDate),
                showDatePicker: initialActivationDate ? "true" : "false",
            });

            const tarifaActual = getTarifaOfertaValue(ofertas_erp, sector_type);
            const modoActual = ofertas_erp.mode_facturacio || ofertas_erp.gas_mode_facturacio;

            if (tarifaActual && modoActual && user?.id) {
                await enableTarifas(tarifaActual, modoActual, user, ofertas_erp.type || "");
                const listaPrecios = getListaPrecios(ofertas_erp, sector_type);
                if (listaPrecios?.[0]) {
                    setValue("tarifa_comercial", listaPrecios[0]);
                }
            }

            hasInitialized.current = true;
        };
        initializeForm();
    }, [ofertas_erp, oferta_id, sector_type, user, enableTarifas, loadSubtypes, reset, setValue]);

    // Quan canvien tarifa/mode, recarrega opcions comercials disponibles.
    // Tambe aplica en creacio de nova oferta (sense oferta_id).
    useEffect(() => {
        const fetchTarifas = async () => {
            if (tarifaOferta && modeFacturacio && user?.id) {
                await enableTarifas(Number(tarifaOferta), modeFacturacio, user, typeOferta);
            }
        };

        // Hacerlo también en nueva oferta (sin oferta_id)
        if (hasInitialized.current || !oferta_id) {
            fetchTarifas();
        }
    }, [tarifaOferta, modeFacturacio, user, oferta_id, enableTarifas, typeOferta]);


    const prevTypeOferta = useRef(null);

    // Restriccio de negoci per call agents: fixa type_oferta a "simple".
    useEffect(() => {
        if (!isRestrictedCallAgent) {
            return;
        }

        if (typeOferta !== 'simple') {
            setValue('type_oferta', 'simple');
            setValue('sub_type_offer', '');
        }
    }, [isRestrictedCallAgent, typeOferta, setValue]);

    // Carrega subtipus quan canvia type_oferta i neteja seleccio anterior.
    useEffect(() => {
        const fetchSubtypes = async () => {
            if (typeOferta && typeOferta !== prevTypeOferta.current) {
                await loadSubtypes(sector_type, typeOferta);
                setValue("sub_type_offer", "");
                prevTypeOferta.current = typeOferta;
            }
        };

        // Permite la carga si ya inicializó o si no hay oferta_id (nueva oferta)
        if (hasInitialized.current || !oferta_id) {
            fetchSubtypes();
        }
    }, [typeOferta, sector_type, oferta_id, loadSubtypes, setValue]);

    // Si hi ha llista de preus i el camp es buit, assigna el valor ERP inicial.
    useEffect(() => {
        const current = getValues("tarifa_comercial");
        if (!current && tarifas && ofertas_erp) {
            const lista = getListaPrecios(ofertas_erp, sector_type);
            if (lista?.[0]) {
                setValue("tarifa_comercial", lista[0]);
            }
        }
    }, [tarifas, tarifaOferta, modeFacturacio, ofertas_erp, sector_type, getValues, setValue]);

    // Cerca/copia un contracte existent quan el flux ho requereix (renovacio/canvi titular).
    const onSearchContrato = useCallback(async () => {
        try {
            const data = getValues();
            if (!data.numero_ctto || !String(data.numero_ctto).trim()) {
                setSubmissionError("Debe informar el número de contrato.");
                return null;
            }
            return await copiar_ctto_actual(
                data.numero_ctto,
                sector_type,
                user.id,
                data.type_oferta,
                data.sub_type_offer
            );
        } catch (error) {
            setSubmissionError("Error al buscar contrato");
            throw error;
        }
    }, [getValues, sector_type, user, setSubmissionError]);

    // Valida estat, construeix payload per enviar al ERP,
    // actualitza oferta existent o en crea una de nova.
    const onSubmit = useCallback(async (data) => {
        if (submitLockRef.current) {
            return { preventDefaultNavigation: true };
        }
        submitLockRef.current = true;
        setIsSubmitting(true);
        setSubmissionError(null);
        try {
            const tarifaComercial = tarifas.find((t) => t.id === Number(data.tarifa_comercial));
            const isCallTarifa = Boolean(tarifaComercial?.name && tarifaComercial.name.toUpperCase().includes("CALL"));
            const elec = sector_type === "electrico";

            let fecha_estimada_inicio_ctto = null;
            if (data.showDatePicker === "true" && data.fecha_estimada_inicio_ctto) {
                fecha_estimada_inicio_ctto = formatDateOnly(data.fecha_estimada_inicio_ctto);
                if (!fecha_estimada_inicio_ctto) {
                    setSubmissionError("Fecha inválida");
                    setIsSubmitting(false);
                    return;
                }
            }

            const marker = "[Activación: Cuanto antes]";
            let observacions = data.observacions || ofertas_erp?.observacions || "";

            if (data.showDatePicker === "false") {
                if (!observacions.includes(marker)) {
                    observacions = `${observacions} ${marker}`.trim();
                }
            } else {
                // Remove marker if it was there and user switched to manual date
                observacions = observacions.replace(marker, "").trim();
            }

            const datos = {
                sector_type,
                type: data.type_oferta,
                sub_type_offer: data.sub_type_offer,
                [elec ? "llista_preu" : "llista_preu_gas"]: tarifaComercial.id,
                llista_preu_global: tarifaComercial.name,
                [elec ? "tarifa_oferta" : "tarifa_oferta_gas"]: data.tarifa_oferta,
                [elec ? "coeficient_k" : "gas_coeficient_k"]: isCallTarifa ? 0 : data.coeficient_k,
                [elec ? "mode_facturacio" : "gas_mode_facturacio"]: data.mode_facturacio,
                [elec ? "fecha_estimada_inicio_ctto" : "fecha_estimada_inicio_ctto_gas"]: fecha_estimada_inicio_ctto,
                mensajeFecha: data.showDatePicker === "true" ? fecha_estimada_inicio_ctto : "Cuanto antes, máximo 30 días",
                observacions: observacions,
            };

            if (elec) {
                datos.coeficient_d = user.coeficient_d;
            }

            const targetOfertaId = oferta_id || createdOfertaIdRef.current;
            
            //Si la oferta ya existe, actualizamos
            if (targetOfertaId) {
                await set_oferta_erp(targetOfertaId, datos, sector_type, data.sub_type_offer, null);
                return { id: targetOfertaId };
            }
            
            //Si no existe, creamos nueva oferta
            const oferta = await create_oferta_erp(user.id, datos); 
            if (oferta?.id) {
                createdOfertaIdRef.current = oferta.id;
            }
            return oferta;
        } catch (error) {
            setSubmissionError("Error al guardar la oferta");
            throw error;
        } finally {
            setIsSubmitting(false);
            submitLockRef.current = false;
        }
    }, [sector_type, oferta_id, user, tarifas, setSubmissionError]);

    return {
        register,
        handleSubmit,
        control,
        watch,
        getValues,
        setValue,
        reset,
        errors,
        typeOferta,
        subTypeOffer,
        modeFacturacio,
        tarifaOferta,
        showDatePicker,
        tarifas,
        loadingTarifas,
        disableTF,
        sub_types_offer_options,
        loadingSubtypes,
        onSearchContrato,
        onSubmit,
        submissionError,
        setSubmissionError,
        isSubmitting,
    };
};

